'use client';

import { useApiMutation } from '@shared/api-utils';
import { FormField } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { DepositLimits as DepositLimitsIcon } from '@shared/icons/system';
import { useClientConfig } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { DepositLimits } from '../../../../../DepositLimits/index';
import { toast } from '../../../../../Toast/Toast';
import { useMultistepForm } from '../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import type { DepositLimitsResponse, LimitPeriod, PlayerLimit, PlayerLimitRequest } from '../../../types';
import { createPlayerLimitRequest, getDepositLimitPath, getDepositLimitsPath } from '../../../utils/playerLimits';
import { getTranslationKeyForPeriod } from '../../utils/utils';
import { useSubmittedValue } from '../SetDepositLimitSheet';

type SetDepositLimitStepProps = {
  limitPeriod: LimitPeriod;
  variant?: 'create' | 'edit';
  originalLimit?: number;
  depositLimits: DepositLimitsResponse | null;
};

type FormData = {
  depositLimits: {
    period: { label: string; value: number };
    amount: string;
  };
};

const SetDepositLimitStep = ({ limitPeriod, variant = 'edit', depositLimits }: SetDepositLimitStepProps) => {
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations('my_account');
  const form = useFormContext<FormData>();
  const { goToNextStep } = useMultistepForm();
  const queryClient = useQueryClient();
  const { currency } = useClientConfig();
  const { setSubmittedValue } = useSubmittedValue();
  const isEditing = variant === 'edit';

  const saveDepositLimit = useApiMutation<PlayerLimit, PlayerLimitRequest>(getDepositLimitPath(limitPeriod), isEditing ? 'PUT' : 'POST', {
    mutationOptions: {
      onError: (error: Error) => {
        debug.forceError('Error updating deposit limit:', error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ['api', getDepositLimitsPath()] });
      },
    },
    requestOptions: {
      credentials: true,
    },
  });

  const handleApply = async () => {
    const isValid = await form.trigger('depositLimits');
    if (!isValid) {
      return;
    }

    const data = form.getValues();
    const depositvalue = Number(data.depositLimits.amount);
    setSubmittedValue(depositvalue);
    saveDepositLimit.mutate(createPlayerLimitRequest(depositvalue, currency), {
      onSuccess: () => {
        goToNextStep();
      },
      onError: () => {
        toast(t('safer_gambling.deposit_limits.add_error_toast'), { type: 'error' });
      },
    });
  };

  // Feature/container/type tracking is set on SetDepositLimitSheet so it wraps Close and updates per step.
  return (
    <div className="flex flex-col h-full" data-testid="set-deposit-limit-step">
      <div className="flex-grow flex flex-col gap-8" data-testid="set-deposit-limit-step-content">
        <div className="flex flex-col gap-2" data-testid="set-deposit-limit-step-intro">
          <DepositLimitsIcon size="extra-extra-large" className="my-3" data-testid="set-deposit-limit-step-icon" />
          <h4>
            {t(
              isEditing
                ? `safer_gambling.deposit_limits.edit_${getTranslationKeyForPeriod(limitPeriod)}_content_header`
                : `safer_gambling.deposit_limits.set_${getTranslationKeyForPeriod(limitPeriod)}_content_header`
            )}
          </h4>
          <p className="paragraph text-foreground-subtle mb-6" data-testid="set-deposit-limit-step-description">
            {t(`pages.set_deposit_limit.set_${getTranslationKeyForPeriod(limitPeriod)}_deposit_limit_step.description`)}
          </p>
        </div>

        <FormField
          control={form.control}
          name="depositLimits"
          render={({ field }) => (
            <DepositLimits
              value={field.value}
              onPeriodChange={option => field.onChange({ ...field.value, period: option })}
              onAmountChange={amount => {
                field.onChange({ ...field.value, amount });
                form.clearErrors('depositLimits.amount');
              }}
              onAmountBlur={field.onBlur}
              depositLimits={depositLimits}
              amountError={form.formState.errors?.depositLimits?.amount?.message}
              fieldIdPrefix="depositLimits"
              onApply={handleApply}
              showDoItLater={false}
              isLoading={saveDepositLimit.isPending}
              singlePeriod={limitPeriod}
              applyLabel={t('pages.set_deposit_limit.save')}
            />
          )}
        />
      </div>
    </div>
  );
};

export { SetDepositLimitStep, type FormData };
